/**
 * The §A.2.2 status vocabulary, in the §4.5 order, with the labels the
 * interface shows. The order is fixed here so the Operational Health bar and
 * the Company World accordion agree with each other and with the server.
 */
export const STATUS_ORDER = [
  'lead_opportunity',
  'estimating_bidding',
  'submitted_pending_award',
  'access_clearance_pending',
  'contracting_mobilization',
  'active_fieldwork',
  'on_hold',
  'punch_substantial_completion',
  'closeout_documentation',
  'final_billing_retainage',
  'audit_records_retention',
  'closed_archived',
  'cancelled',
  'not_awarded',
  'terminated',
] as const;

export type ProjectStatus = (typeof STATUS_ORDER)[number];

export const STATUS_LABEL: Record<string, string> = {
  lead_opportunity: 'Lead / opportunity',
  estimating_bidding: 'Estimating / bidding',
  submitted_pending_award: 'Submitted, pending award',
  access_clearance_pending: 'Access / clearance pending',
  contracting_mobilization: 'Contracting / mobilization',
  active_fieldwork: 'Active fieldwork',
  on_hold: 'On hold',
  punch_substantial_completion: 'Punch / substantial completion',
  closeout_documentation: 'Closeout documentation',
  final_billing_retainage: 'Final billing / retainage',
  audit_records_retention: 'Audit / records retention',
  closed_archived: 'Closed / archived',
  cancelled: 'Cancelled',
  not_awarded: 'Not awarded',
  terminated: 'Terminated',
};

export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status.replace(/_/g, ' ');
}

/**
 * Segment fills for the §1.2.1 bar.
 *
 * Every fill is a royal-blue or navy step, so no segment ever renders gold on
 * a light surface. Terminal and adverse statuses are the grey end of the
 * ramp: the bar shows where work *is*, and dimming what is finished or lost
 * is the whole reason the widget pre-filters to active fieldwork.
 */
export const STATUS_FILL: Record<string, string> = {
  lead_opportunity: 'hsl(219 30% 72%)',
  estimating_bidding: 'hsl(219 45% 62%)',
  submitted_pending_award: 'hsl(219.6 53% 50.4%)',
  access_clearance_pending: 'hsl(220 70% 42%)',
  contracting_mobilization: 'hsl(220 90% 35%)',
  active_fieldwork: 'hsl(220 100% 30%)',
  on_hold: 'hsl(26 35% 42%)',
  punch_substantial_completion: 'hsl(215 70% 26%)',
  closeout_documentation: 'hsl(212 80% 20%)',
  final_billing_retainage: 'hsl(210.5 100% 16%)',
  audit_records_retention: 'hsl(210.5 100% 12.4%)',
  closed_archived: 'hsl(215 12% 55%)',
  cancelled: 'hsl(215 10% 68%)',
  not_awarded: 'hsl(215 8% 72%)',
  terminated: 'hsl(3 40% 42%)',
};

export function statusFill(status: string): string {
  return STATUS_FILL[status] ?? 'hsl(215 12% 55%)';
}

/** hsl(H S% L%) to an [r, g, b] triple in 0-255. */
function hslToRgb(css: string): [number, number, number] {
  const match = /hsl\(([\d.]+) ([\d.]+)% ([\d.]+)%\)/.exec(css);
  if (!match) return [128, 128, 128];
  const h = Number(match[1]);
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg = Math.floor(h / 60) % 6;
  const base = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ][seg]!;
  return [
    Math.round((base[0]! + m) * 255),
    Math.round((base[1]! + m) * 255),
    Math.round((base[2]! + m) * 255),
  ];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const f = (v: number): number => {
    const channel = v / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/**
 * The paint for one segment of the §1.2.1 health bar.
 *
 * §1.2.1 asks for the pre-filtered segment at full saturation and the rest
 * recessed. Recessing the whole button with `opacity` also recesses the count
 * printed on it: measured on the rendered page, an unselected count came out
 * at 1.57:1 against its own fill, which is an unreadable number rather than a
 * quiet one. The fill is therefore mixed toward white while the count stays at
 * full strength, and the ink is chosen from the luminance of the mixed fill so
 * it is legible at both ends of the ramp.
 */
export function segmentPaint(
  status: string,
  active: boolean,
): { background: string; color: string } {
  const rgb = hslToRgb(statusFill(status));
  const mix = active ? 1 : 0.28;
  const blended: [number, number, number] = [
    Math.round(rgb[0] * mix + 255 * (1 - mix)),
    Math.round(rgb[1] * mix + 255 * (1 - mix)),
    Math.round(rgb[2] * mix + 255 * (1 - mix)),
  ];
  // Contrast against white is 1.05 / (L + 0.05); against the navy ink it is
  // (L + 0.05) / 0.0675. The crossover sits just above L = 0.19.
  const color = relativeLuminance(blended) > 0.19 ? '#001F3F' : '#FFFFFF';
  return { background: `rgb(${blended.join(', ')})`, color };
}

/** The §4.5 health flags, in the words a superintendent would use. */
export const FLAG_LABEL: Record<string, string> = {
  schedule_at_risk: 'Schedule at risk',
  over_budget: 'Over budget',
  blocked: 'Blocked',
  compliance_lapse: 'Compliance lapse',
};

/**
 * Money, or an honest blank. A `null` figure means no `project_financials`
 * row exists; it is never rendered as `$0`, because a zero contract sum and an
 * unknown contract sum are different facts and only one of them is true.
 */
export function money(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
